/* Legal/Trust content — Phase 10c-C draft-quality copy.
 *
 * IMPORTANT: All content here is a product-ready DRAFT. Final wording must be
 * reviewed by qualified legal counsel before production launch, especially for:
 *   • DPDP Act, 2023 compliance (India)
 *   • student/child data handling
 *   • refund/cancellation rules
 *   • international transfer
 *   • marketplace hiring obligations
 *   • GST + invoicing
 *   • Razorpay onboarding obligations
 *
 * Last reviewed: 2026-06-14 · Version: 1.0-draft
 */

export const COMPANY = {
  legal_name: "Factskoot Eduwellness Private Limited",
  product: "TeeBee AI",
  website: "factskoot.com",
  address: "Factskoot Eduwellness Pvt Ltd, No 43, WeWork Galaxy, Shanthala Nagar, Residency Road, Bengaluru – 560025, Karnataka, India",
  governing_law: "India",
  jurisdiction: "Bengaluru, Karnataka, India",
  emails: {
    support: "support@teebee.ai",
    privacy: "privacy@teebee.ai",
    billing: "billing@teebee.ai",
    business: "info@factskoot.com",
  },
  whatsapp: "+91 9844139333",
  recognitions: [
    "Startup India recognised · DPIIT: DIPP104056",
    "ISO 9001:2015 certified",
    "ISO 21001:2018 certified",
  ],
  grievance_officer: "To be updated before production launch.",
};

const FOOTNOTE = `${COMPANY.product} is a product of ${COMPANY.legal_name}.`;

const PAYMENT_DISCLOSURE = [
  "Payments are processed securely through Razorpay.",
  "All domestic and international payments are in Indian Rupees (INR).",
  "TeeBee Coins are internal platform credits only — not cryptocurrency, not legal tender, not an investment, not withdrawable.",
  "Used TeeBee Coins are generally non-refundable.",
  "Failed or duplicate payments may be reviewed for refund through support.",
];

/* ─────────────────────────────────────────────────────────────────────────── */

export const LEGAL_PAGES = {
  /* ============================================================ TERMS */
  "terms": {
    slug: "terms",
    title: "Terms and Conditions",
    eyebrow: "Legal",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: `These Terms govern your use of ${COMPANY.product}, operated by ${COMPANY.legal_name} ("we", "us"). By creating an account or using the service you agree to be bound by these Terms.`,
    sections: [
      { id: "acceptance", title: "1. Acceptance of Terms", body: [
        "By accessing or using TeeBee AI (the 'Service') you confirm that you have read, understood and agree to these Terms, our Privacy Policy, Refund Policy, AI Disclaimer and all referenced policies.",
        "If you do not agree, please do not use the Service.",
      ]},
      { id: "eligibility", title: "2. Eligibility", body: [
        "You must be at least 18 years old, or have lawful authority to enter into a binding contract in your jurisdiction.",
        "Institutional/school accounts may be created by authorised representatives only.",
      ]},
      { id: "accounts", title: "3. Account types & responsibility", body: [
        "Educator accounts — individual teachers using TeeBee for lesson planning, OCR, assessments, profile building and marketplace participation.",
        "Hiring / recruiter accounts — schools, institutions and recruiters posting jobs, accessing CVs (subject to consent) and managing applications.",
        "Institution accounts — schools or organisations bringing multiple teachers onto TeeBee under one workspace.",
        "You are responsible for the security of your credentials and all activity on your account.",
      ]},
      { id: "ai-content", title: "4. AI-generated content disclaimer", body: [
        "TeeBee uses AI-assisted tools to generate lesson plans, worksheets, MCQs, rubrics, resumes, cover letters, career advice and other content.",
        "AI outputs may contain errors, inaccuracies, omissions or content that requires curriculum/age-appropriateness review.",
        "You are responsible for reviewing AI outputs before classroom use, hiring decisions, public publication, or any binding action.",
        "TeeBee does not provide medical, legal, financial or psychological advice. Wellbeing features are supportive and non-clinical.",
      ]},
      { id: "marketplace", title: "5. Educator Marketplace usage", body: [
        "You may publish your educator profile, demo lessons, resume and lesson packs subject to your visibility settings.",
        "Hiring users may request access to your CV; you control approval and revocation.",
        "TeeBee does not guarantee hiring outcomes or candidate suitability. Institutions must independently verify qualifications.",
      ]},
      { id: "ip", title: "6. Intellectual property & user content", body: [
        "Content you create using TeeBee (lessons, worksheets, resumes, profile) belongs to you.",
        "When you publish content publicly, you grant TeeBee a non-exclusive, worldwide licence to host, display, distribute and back up that content while it remains public.",
        "TeeBee branding, logos, copy and software code are owned by Factskoot Eduwellness Private Limited.",
      ]},
      { id: "prohibited", title: "7. Prohibited activities", body: [
        "You may not: scrape data, abuse the API, attempt to extract another user's private data, post misleading credentials, post fake or discriminatory job posts, share student personal data publicly, upload illegal/harmful content, or attempt to reverse-engineer the Service.",
      ]},
      { id: "coins", title: "8. TeeBee Coins, plans & billing", body: [
        "TeeBee Coins are internal platform credits used to access premium features. See the TeeBee Coins Policy for details.",
        ...PAYMENT_DISCLOSURE,
        "Refunds and cancellations are governed by the Refund and Cancellation Policy.",
      ]},
      { id: "termination", title: "9. Suspension & termination", body: [
        "We may suspend or terminate your account for material breach of these Terms or applicable law, with reasonable notice except in cases of fraud, abuse or legal compulsion.",
        "You may close your account at any time from Settings.",
      ]},
      { id: "liability", title: "10. Limitation of liability", body: [
        "To the maximum extent permitted by law, TeeBee's aggregate liability for any claim is limited to the amount you paid us in the twelve months preceding the claim.",
        "TeeBee is not liable for indirect, incidental, special, consequential or punitive damages.",
      ]},
      { id: "changes", title: "11. Changes to these Terms", body: [
        "We may update these Terms from time to time. Material changes will be notified by email and via in-app notice at least 7 days before they take effect.",
      ]},
      { id: "law", title: "12. Governing law & jurisdiction", body: [
        `These Terms are governed by the laws of ${COMPANY.governing_law}. Courts at ${COMPANY.jurisdiction} have exclusive jurisdiction subject to applicable consumer law.`,
      ]},
      { id: "contact", title: "13. Contact", body: [
        `Questions? Reach us at ${COMPANY.emails.support}. Address: ${COMPANY.address}.`,
      ]},
    ],
  },

  /* ============================================================ PRIVACY */
  "privacy": {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Legal",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: `${COMPANY.product} is operated by ${COMPANY.legal_name}. This Privacy Policy is designed to support privacy-first practices and is aligned with the Digital Personal Data Protection Act, 2023 (India). Final compliance certification is subject to legal review.`,
    sections: [
      { id: "data-collected", title: "1. Data we collect", body: [
        "Account data — name, email, phone (optional), password hash, language preference.",
        "Educator profile data — bio, subjects, curriculums, experience, education, certifications, achievements, languages, demo links.",
        "CV / resume data — generated or uploaded resumes, file attachments, hiring availability.",
        "Marketplace data — public profile selections, visibility toggles, application history.",
        "Hiring user data — institution details, job posts, CV access requests.",
        "Payment metadata — Razorpay transaction IDs, pack purchased, INR amount. We do NOT store full card details.",
        "Usage analytics — feature usage, generation counts, page views (subject to cookie consent).",
        "AI generation data — your prompts, generated outputs, OCR extracts. Retained to power your library and as a service quality control input.",
        "Uploaded documents — scanned worksheets, photos for OCR, attachments.",
      ]},
      { id: "why", title: "2. Why we collect it", body: [
        "To operate and improve TeeBee AI — running AI generations, storing your library, surfacing your portfolio.",
        "To deliver hiring opportunities only with your consent.",
        "To process payments and prevent fraud.",
        "To communicate service updates, support replies and (optionally) marketing newsletters you can unsubscribe from.",
        "To comply with legal obligations.",
      ]},
      { id: "consent", title: "3. Your consent & control", body: [
        "Profile is private by default. You toggle public/private visibility at any time.",
        "CV is hidden unless you publish it.",
        "Contact details (phone/email) are masked unless you explicitly choose to share.",
        "Recruiters can request CV access; you approve or reject each request.",
        "You may withdraw consent at any time from Settings → Privacy.",
      ]},
      { id: "sharing", title: "4. Sharing & third-party processors", body: [
        "Payment gateway — Razorpay processes transactions on our behalf.",
        "Cloud infrastructure — hosting, database and email delivery providers under standard processing agreements.",
        "Hiring users — only the profile fields you explicitly publish/share, and your CV only after you approve a CV access request.",
        "Legal compliance — we may disclose data when required by law, court order, or to protect rights and safety.",
      ]},
      { id: "security", title: "5. Security", body: [
        "Passwords are hashed with industry-standard algorithms; we never store them in plain text.",
        "Auth tokens use httpOnly cookies (XSS-resistant).",
        "Transport encryption (HTTPS/TLS) on all endpoints.",
        "Role-based access for institutional/admin features.",
      ]},
      { id: "retention", title: "6. Data retention", body: [
        "Active accounts — retained while the account is active.",
        "Closed accounts — anonymised within 90 days unless required for legal/financial record keeping.",
        "Payment records — retained for the statutory period required by Indian tax and financial law.",
        "AI prompts/outputs — you can delete from your library at any time.",
      ]},
      { id: "rights", title: "7. Your rights", body: [
        "Access — view the data we hold via Settings → Export.",
        "Correction — update inaccurate data from Profile / Settings.",
        "Erasure — request deletion via privacy@teebee.ai.",
        "Portability — export your library and resume.",
        "Grievance — write to our grievance officer (see Contact).",
      ]},
      { id: "children", title: "8. Children & student data", body: [
        "TeeBee is not intended for children under 18 to create accounts directly.",
        "Educators may upload student work for OCR/grading; we strongly recommend anonymising student personal data before upload.",
        "See the Child / Student Data Policy for full guidelines.",
      ]},
      { id: "international", title: "9. International users", body: [
        "TeeBee is operated from India. By using the Service from outside India you consent to the transfer of your data to India for processing in accordance with this Policy.",
      ]},
      { id: "contact", title: "10. Contact & grievance", body: [
        `Privacy queries: ${COMPANY.emails.privacy}. Postal: ${COMPANY.address}. Grievance officer: ${COMPANY.grievance_officer}`,
      ]},
    ],
  },

  /* ============================================================ REFUND */
  "refund-policy": {
    slug: "refund-policy",
    title: "Refund and Cancellation Policy",
    eyebrow: "Legal · Razorpay",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: `This policy applies to all purchases of TeeBee Coins, plans and digital services from ${COMPANY.product} (${COMPANY.legal_name}). All payments are processed via Razorpay in Indian Rupees (INR).`,
    sections: [
      { id: "scope", title: "1. Scope", body: [
        "TeeBee sells digital credits (TeeBee Coins), subscription plans and digital services. No physical goods are shipped.",
      ]},
      { id: "coins", title: "2. TeeBee Coins", body: [
        "TeeBee Coins are internal platform credits — NOT crypto, NOT cash, NOT investment, NOT withdrawable.",
        "Consumed coins (used to generate a lesson, worksheet, resume, etc.) are non-refundable.",
        "Promotional and referral coins are non-refundable.",
        "If an AI generation fails on our side, the coins charged for that action are automatically refunded to your wallet — this is reflected in your transaction history as a 'Refund'.",
      ]},
      { id: "failed-duplicate", title: "3. Failed or duplicate payments", body: [
        "Failed payments — handled automatically per Razorpay/bank settlement. No coins are credited; bank reversal usually completes within 5–10 working days.",
        "Duplicate payments — eligible for review and refund. Email billing@teebee.ai with both transaction IDs.",
      ]},
      { id: "subscriptions", title: "4. Subscription cancellation", body: [
        "You may cancel any subscription at any time from Settings → Plans. Cancellation stops future renewals.",
        "Unused portions of an already-paid subscription period are non-refundable unless required by applicable law or expressly approved by support.",
      ]},
      { id: "institution", title: "5. Institution plans", body: [
        "Institution plans are governed by the specific agreement / invoice terms signed at purchase. Refund rules in that agreement override this default policy.",
      ]},
      { id: "abuse", title: "6. Abuse & fraud", body: [
        "Refunds may be withheld and accounts may be suspended in cases of suspected fraud, chargeback abuse, or repeated policy violations.",
      ]},
      { id: "timeline", title: "7. Refund timeline", body: [
        "Approved refunds are issued back to the original payment instrument via Razorpay.",
        "Typical settlement: 5–10 working days, subject to your bank.",
      ]},
      { id: "contact", title: "8. Contact for refunds", body: [
        `Billing & refunds: ${COMPANY.emails.billing}. Include your registered email, transaction ID, and reason.`,
      ]},
    ],
  },

  /* ============================================================ SHIPPING (digital) */
  "shipping-delivery": {
    slug: "shipping-delivery",
    title: "Shipping & Delivery Policy (Digital Services)",
    eyebrow: "Legal · Razorpay",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: `${COMPANY.product} is a digital SaaS product. No physical goods are shipped.`,
    sections: [
      { id: "nature", title: "1. Digital delivery", body: [
        "TeeBee delivers all services digitally through the web application and (where applicable) email.",
      ]},
      { id: "credits", title: "2. TeeBee Coins activation", body: [
        "TeeBee Coins are credited to your in-product wallet immediately after Razorpay confirms a successful payment.",
        "If your bank/Razorpay places a payment on hold, activation is delayed until that confirmation completes.",
      ]},
      { id: "subscriptions", title: "3. Plan activation", body: [
        "Subscription plans activate immediately after successful payment.",
      ]},
      { id: "invoices", title: "4. Invoices & receipts", body: [
        "Invoices/receipts are delivered to your registered email and available in Settings → Billing.",
      ]},
      { id: "timing", title: "5. Estimated activation time", body: [
        "Successful Razorpay payments — within 60 seconds (real-time webhook).",
        "On-hold/disputed payments — pending Razorpay/bank confirmation, usually 1–3 working days.",
      ]},
      { id: "issues", title: "6. Delivery issues", body: [
        `If you completed a payment but coins/plan did not activate within 24 hours, email ${COMPANY.emails.billing} with your transaction ID.`,
      ]},
    ],
  },

  /* ============================================================ PRICING / BILLING */
  "pricing-billing": {
    slug: "pricing-billing",
    title: "Pricing and Billing Policy",
    eyebrow: "Legal · Razorpay",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "Transparent, educator-friendly pricing. All charges in INR, processed via Razorpay.",
    sections: [
      { id: "plans", title: "1. Plan types", body: [
        "Free — every teacher starts free with 50 TeeBee Coins on signup and ongoing free access to basic AI chat, basic lesson plans, MCQ generation and OCR scans.",
        "TeeBee Pro — premium feature access via TeeBee Coins or future subscription.",
        "Institution — multi-seat workspaces with consolidated billing (custom-quoted).",
        "Creator — for educators publishing on the Resource Marketplace (future).",
      ]},
      { id: "packs", title: "2. TeeBee Coins recharge packs", body: [
        "Starter — 25 coins for ₹99",
        "Plus — 75 coins for ₹249",
        "Pro — 200 coins for ₹599",
        "Power — 500 coins for ₹1,299",
        "School — 1,500 coins for ₹3,499",
        "Institute — 5,000 coins for ₹4,999",
      ]},
      { id: "tax", title: "3. Taxes (GST)", body: [
        "Where applicable, GST will be added on top of the listed price as per Indian tax law. Final tax-inclusive amount is shown at checkout.",
      ]},
      { id: "invoices", title: "4. Invoices", body: [
        "GST-compliant invoices are issued to all paying users at the registered email.",
      ]},
      { id: "renewal", title: "5. Renewal & cancellation", body: [
        "Subscriptions (where applicable) renew automatically unless cancelled from Settings → Plans.",
      ]},
      { id: "changes", title: "6. Price changes", body: [
        "Material price changes will be communicated by email at least 14 days in advance.",
      ]},
      { id: "disclosure", title: "7. Payment disclosure", body: PAYMENT_DISCLOSURE},
    ],
  },

  /* ============================================================ COINS POLICY */
  "coins-policy": {
    slug: "coins-policy",
    title: "TeeBee Coins Policy",
    eyebrow: "Legal",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "TeeBee Coins are internal platform credits used to access premium AI features. They are not cryptocurrency, not legal tender, not an investment, not withdrawable.",
    sections: [
      { id: "what", title: "1. What TeeBee Coins are (and are not)", body: [
        "Internal platform credits redeemable only inside TeeBee.",
        "Not cryptocurrency or blockchain tokens.",
        "Not a currency, security or investment instrument.",
        "Not withdrawable, transferable outside TeeBee, or tradable on any exchange.",
      ]},
      { id: "usage", title: "2. How coins are used", body: [
        "Premium AI features — Full Teaching Kit, Auto-Differentiated Pack, detailed lesson plans, worksheets, assessments, AI Resume, Career Advice, Cover Letter, Profile Boost.",
        "OCR smart actions and document AI.",
        "Marketplace boosts (when available).",
      ]},
      { id: "transparency", title: "3. Transparent pricing & deduction", body: [
        "Before every paid action, TeeBee shows: 'This will use X TeeBee Coins.'",
        "After completion: 'X TeeBee Coins used.' — written to your transaction history.",
        "No hidden deductions.",
      ]},
      { id: "refund", title: "4. Failure refunds", body: [
        "If an AI generation fails on our side, charged coins are automatically refunded to your wallet.",
      ]},
      { id: "promo", title: "5. Free, promotional & referral coins", body: [
        "Signup grant — 50 TeeBee Coins on account creation.",
        "Referral rewards — coins for referrer and referee per the Referral Programme.",
        "Promotional coins (campaigns) — non-refundable and may carry expiry rules.",
      ]},
      { id: "abuse", title: "6. Abuse & fraud", body: [
        "Coins obtained through fraud, chargeback abuse, or policy violation may be reversed and the account suspended.",
      ]},
      { id: "lifecycle", title: "7. Lifecycle", body: [
        "Paid coins — currently no expiry. Future expiry rules, if introduced, will be communicated with at least 60 days' notice.",
        "Promotional/referral coins — may carry an expiry per the specific campaign terms.",
      ]},
    ],
  },

  /* ============================================================ MARKETPLACE POLICY */
  "marketplace-policy": {
    slug: "marketplace-policy",
    title: "Marketplace Policy",
    eyebrow: "Marketplace",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "Rules of the road for the TeeBee Educator Marketplace and Hiring Network — built for educators and the schools that want to hire them.",
    sections: [
      { id: "categories", title: "1. Categories", body: [
        "Curricular subjects (Math, Science, Languages, Humanities, etc.) and extra-curricular tracks (MUN, debate, music, robotics, sports, arts).",
        "Educator profiles, demo lessons, lesson packs and resume listings.",
      ]},
      { id: "recruiter", title: "2. Hiring user obligations", body: [
        "Post only genuine, lawful job opportunities. No misleading titles, no bait-and-switch.",
        "Do NOT scrape, harvest or repurpose teacher data outside TeeBee.",
        "Do NOT contact teachers using leaked contact info — use TeeBee in-app communication.",
        "Maintain non-discriminatory hiring practices in line with applicable law.",
      ]},
      { id: "profile", title: "3. Educator profile accuracy", body: [
        "Keep your profile, qualifications and achievements truthful. Misrepresentation may result in account suspension.",
      ]},
      { id: "cv-access", title: "4. CV access flow", body: [
        "Recruiters must request CV access; teachers approve or reject each request explicitly.",
        "Once approved, CV download/access is logged for audit.",
      ]},
      { id: "verification", title: "5. Verification badges", body: [
        "Email/Phone/Profile/Institution/Certificate Verified badges indicate that TeeBee performed a procedural check — they are NOT a substitute for the institution's own due diligence.",
      ]},
      { id: "outcomes", title: "6. Hiring outcomes", body: [
        "TeeBee does not guarantee hiring outcomes or candidate suitability. Institutions must independently verify qualifications.",
      ]},
      { id: "prohibited", title: "7. Prohibited job posts", body: [
        "Discriminatory listings (by race, religion, gender beyond bona-fide occupational requirements, disability), MLM/pyramid schemes, vague gig posts without compensation, and any post violating applicable labour or anti-discrimination law.",
      ]},
      { id: "moderation", title: "8. Reporting & moderation", body: [
        `Report abuse to ${COMPANY.emails.support}. We review and may unpublish profiles/posts in violation.`,
      ]},
    ],
  },

  /* ============================================================ PROFILE / CV VISIBILITY */
  "profile-visibility-policy": {
    slug: "profile-visibility-policy",
    title: "Educator Profile and CV Visibility Policy",
    eyebrow: "Trust & Privacy",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "You own your profile. TeeBee gives you granular control over what becomes public and who can see your CV.",
    sections: [
      { id: "private-by-default", title: "1. Private by default", body: [
        "New profiles are private. Nothing is visible to the public until you explicitly choose to publish.",
      ]},
      { id: "controls", title: "2. Visibility controls", body: [
        "Public profile toggle — show profile on the Marketplace.",
        "CV publish toggle — make your CV visible to logged-in hiring users.",
        "Allow CV download — separate toggle from visibility.",
        "Recruiter-only mode — hide from anonymous Explore.",
        "Hidden contact details unless permitted.",
      ]},
      { id: "cv-access", title: "3. CV access requests", body: [
        "Hiring users must request CV access. You approve or reject; revocation is one click.",
        "Each access is logged in your activity history.",
      ]},
      { id: "share-links", title: "4. Public share links", body: [
        "When you publish your profile, a /u/<username> link is created. You can unpublish at any time — the link will then show a 'not available' state.",
      ]},
      { id: "responsibility", title: "5. Your responsibility", body: [
        "Keep your published data accurate and lawful. Do NOT publish other people's personal data without consent.",
      ]},
    ],
  },

  /* ============================================================ RECRUITER POLICY */
  "recruiter-policy": {
    slug: "recruiter-policy",
    title: "Recruiter & Hiring User Policy",
    eyebrow: "Marketplace",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "Rules for schools, colleges, recruiters and activity academies using TeeBee's hiring network.",
    sections: [
      { id: "eligibility", title: "1. Eligibility", body: [
        "Schools, colleges, training institutes, activity academies, education recruiters, and authorised representatives thereof.",
      ]},
      { id: "accuracy", title: "2. Accurate job posting", body: [
        "Posts must accurately describe the role, location, salary band (or 'TBD'), curriculum and required qualifications.",
      ]},
      { id: "anti-spam", title: "3. Anti-spam & anti-harassment", body: [
        "No mass-messaging educators with off-platform contact. No harassment. No discriminatory job postings.",
      ]},
      { id: "cv-handling", title: "4. CV handling", body: [
        "Use approved-access CVs only for the role specified. Do NOT share, repost or sell CVs outside your hiring team.",
        "Delete CV files when the application is closed (3 months from rejection by default).",
      ]},
      { id: "decisions", title: "5. Hiring decisions & verification", body: [
        "You remain solely responsible for hiring decisions, qualification verification, background checks, and offer terms.",
      ]},
      { id: "suspension", title: "6. Suspension for misuse", body: [
        "Misuse — including CV scraping, off-platform poaching of contact info, or discriminatory posting — results in account suspension and may be reported to authorities.",
      ]},
    ],
  },

  /* ============================================================ AI DISCLAIMER */
  "ai-disclaimer": {
    slug: "ai-disclaimer",
    title: "AI Usage Disclaimer",
    eyebrow: "AI",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "TeeBee uses AI-assisted tools to support educators. AI is a co-pilot, not an authority. You remain in control of every output.",
    sections: [
      { id: "errors", title: "1. AI outputs may contain errors", body: [
        "Lesson plans, MCQs, worksheets, rubrics and resumes generated by TeeBee may contain factual errors, omissions, or content that requires curriculum/age-appropriateness review.",
      ]},
      { id: "review", title: "2. Review before use", body: [
        "Review every output before classroom use, public publication, hiring decision or any binding action.",
      ]},
      { id: "ocr", title: "3. OCR accuracy", body: [
        "OCR (text extraction from images / handwriting) may misread characters, especially for cursive handwriting, low-contrast scans or non-Latin scripts.",
      ]},
      { id: "matching", title: "4. Job matching is assistive", body: [
        "AI job matching surfaces likely-fit candidates and roles. It is NOT a substitute for human judgement; final hiring decisions are the institution's.",
      ]},
      { id: "wellbeing", title: "5. Wellbeing is supportive, not clinical", body: [
        "Wellbeing nudges and check-ins are supportive only. TeeBee does NOT provide medical, psychological, legal or financial advice and does not diagnose conditions.",
      ]},
      { id: "branding", title: "6. AI provider branding", body: [
        "TeeBee AI is the user-facing name of our AI capabilities (TeeBee Intelligence, TeeBee Vision, TeeBee Voice). We use industry-standard model providers under appropriate agreements; their branding does not appear in our product surface.",
      ]},
    ],
  },

  /* ============================================================ CONTENT OWNERSHIP */
  "content-policy": {
    slug: "content-policy",
    title: "Content Ownership Policy",
    eyebrow: "Legal",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "You own your educator content. When you publish publicly, you grant TeeBee a limited licence to host, display, distribute and back up that content.",
    sections: [
      { id: "ownership", title: "1. Teacher-created content", body: [
        "Lessons, worksheets, rubrics, resumes and profile content you create with TeeBee belong to you.",
      ]},
      { id: "ai-output", title: "2. AI-generated content", body: [
        "AI outputs you generate are yours to use for your educational purposes. Verify accuracy and copyright before commercial use.",
      ]},
      { id: "public", title: "3. Public lessons & marketplace listings", body: [
        "Publishing publicly grants TeeBee a worldwide, royalty-free, non-exclusive licence to host, display, distribute and back up the content while it remains public.",
        "Other educators may duplicate or remix public lessons; original attribution is preserved.",
      ]},
      { id: "official", title: "4. TeeBee Official content", body: [
        "Seed lessons and content authored by TeeBee remain owned by TeeBee. Public lessons created by Top Creators retain creator ownership.",
      ]},
      { id: "takedown", title: "5. Takedown & copyright", body: [
        `Copyright infringement claims: email ${COMPANY.emails.support} with the URL, your contact info, the original work and a good-faith statement. We review and act within 7 working days.`,
      ]},
      { id: "unpublish", title: "6. Unpublishing", body: [
        "You can unpublish your public content at any time from Library → Publish toggle.",
      ]},
    ],
  },

  /* ============================================================ COMMUNITY */
  "community-guidelines": {
    slug: "community-guidelines",
    title: "Community Guidelines",
    eyebrow: "Community",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "TeeBee is built for educators. These guidelines keep the community safe, respectful and useful.",
    sections: [
      { id: "respect", title: "1. Be respectful", body: [
        "No harassment, hate speech, threats, doxxing or personal attacks.",
        "Differing pedagogical opinions are welcome; personal attacks are not.",
      ]},
      { id: "honesty", title: "2. Be honest", body: [
        "No misleading credentials, certifications, or institution affiliations.",
        "No fake job posts.",
      ]},
      { id: "originality", title: "3. Respect originality", body: [
        "No plagiarism. Credit the original creator when remixing public lessons.",
      ]},
      { id: "student-safety", title: "4. Protect student safety", body: [
        "Never share identifiable student personal data publicly.",
        "Anonymise answer sheets before posting publicly.",
      ]},
      { id: "spam", title: "5. No spam", body: [
        "Promotional posts, off-topic links, or repeated content may be removed.",
      ]},
      { id: "report", title: "6. Reporting", body: [
        `Report violations: ${COMPANY.emails.support}. We review every report.`,
      ]},
    ],
  },

  /* ============================================================ STUDENT DATA */
  "student-data-policy": {
    slug: "student-data-policy",
    title: "Child / Student Data Policy",
    eyebrow: "Safety",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "Student data is sensitive. This policy guides educators on safe handling within TeeBee.",
    sections: [
      { id: "minimise", title: "1. Minimise student personal data", body: [
        "Avoid uploading unnecessary student personal data. Anonymise answer sheets where possible (cover names, roll numbers, photos).",
      ]},
      { id: "consent", title: "2. Authority & consent", body: [
        "Do not upload sensitive student data without appropriate authority or parental consent under your institution's policy.",
        "Institution administrators are responsible for ensuring teacher use complies with local data-protection law and school policy.",
      ]},
      { id: "scope", title: "3. Scope of processing", body: [
        "TeeBee processes uploaded materials only for the feature requested (OCR, grading, summarisation). We do not use student data to train external AI models.",
      ]},
      { id: "no-public", title: "4. No public sharing", body: [
        "Never publish identifiable student personal data on TeeBee public surfaces (lessons, marketplace, profile).",
      ]},
      { id: "deletion", title: "5. Deletion", body: [
        "Delete student-related uploads from your library once their educational purpose is served.",
      ]},
    ],
  },

  /* ============================================================ COOKIES */
  "cookie-policy": {
    slug: "cookie-policy",
    title: "Cookie Policy",
    eyebrow: "Privacy",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: "TeeBee uses cookies and similar technologies to keep you logged in, remember your preferences, and (with consent) understand how the product is used.",
    sections: [
      { id: "types", title: "1. Cookie types", body: [
        "Essential — required for the app to function (authentication, session, security). These cannot be disabled.",
        "Preference — remember language, theme, dismissed notices.",
        "Analytics — aggregate, anonymised usage to improve the product. Loaded only after consent.",
        "Performance — page load metrics. Loaded only after consent.",
      ]},
      { id: "consent", title: "2. Your choices", body: [
        "Accept all — enable preference + analytics + performance cookies.",
        "Reject non-essential — only essential cookies are loaded.",
        "Manage — fine-grained control via the cookie banner.",
        "Change preferences anytime via Settings → Privacy.",
      ]},
      { id: "third-party", title: "3. Third parties", body: [
        "Payment cookies set by Razorpay during checkout — limited to the checkout flow.",
      ]},
    ],
  },

  /* ============================================================ CONTACT */
  "contact": {
    slug: "contact",
    title: "Contact & Grievance",
    eyebrow: "Support",
    last_updated: "2026-06-14",
    version: "1.0-draft",
    intro: `We're a small, focused team. ${COMPANY.product} is built by ${COMPANY.legal_name}.`,
    sections: [
      { id: "company", title: "Company", body: [
        `Legal name: ${COMPANY.legal_name}`,
        `Product: ${COMPANY.product}`,
        `Website: ${COMPANY.website}`,
        `Registered address: ${COMPANY.address}`,
        ...COMPANY.recognitions,
      ]},
      { id: "channels", title: "How to reach us", body: [
        `General support: ${COMPANY.emails.support}`,
        `Privacy / grievance: ${COMPANY.emails.privacy}`,
        `Billing / refunds: ${COMPANY.emails.billing}`,
        `Business / partnerships: ${COMPANY.emails.business}`,
        `WhatsApp: ${COMPANY.whatsapp}`,
      ]},
      { id: "response", title: "Response timeline", body: [
        "Support — usually within 1 working day.",
        "Privacy / data requests — within 30 days as per DPDP Act, 2023.",
        "Grievance escalation — through our grievance officer (see below).",
      ]},
      { id: "grievance", title: "Grievance officer", body: [
        COMPANY.grievance_officer,
        `Escalation email: ${COMPANY.emails.privacy}`,
      ]},
    ],
  },
};

export const LEGAL_FOOTNOTE = FOOTNOTE;
export const PAYMENT_FOOTNOTE = PAYMENT_DISCLOSURE;
